import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { ThreeDots } from "react-loader-spinner";
import { FaRegEyeSlash, FaRegEye } from "react-icons/fa";
import { CiSearch } from "react-icons/ci";
import { IoChevronDownSharp } from "react-icons/io5";
import { useAuth } from "../../context/Auth";
import { showApiErrors } from "../../utils/errorHandler";

const AddUserModal = ({ show, onClose }) => {
    const { requestWithAuth } = useAuth();
    const [data, setData] = useState({});
    const [registeredMembers, setRegisteredMembers] = useState([]);
    const [creatingAccount, setCreatingAccount] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
    const [memberSearch, setMemberSearch] = useState("");
    const [searching, setSearching] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    const handleChange = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.value,
        });
    };

    const getRegisteredMembers = async (searchValue) => {
        try {
            setSearching(true)
            const response = await requestWithAuth(`/members/?search=${searchValue}&paginated=false`, "GET");
            console.log(response)
            setRegisteredMembers(response?.data?.members);
        } catch (error) {
            console.error(error);
        }finally{
            setSearching(false)
        }
    }

    

    const handleMemberSelect = (member) => {
        setSelectedMember(member);
        setData({
            ...data,
            member_account: member.id
        });
        setMemberDropdownOpen(false);
        setMemberSearch("");
    };

    const createUser = async (e) => {
        e.preventDefault();
        try {
            const body = JSON.stringify(data);
            setCreatingAccount(true);
            const response = await requestWithAuth("/accounts/users/", "POST", body, {
                "Content-Type": "application/json",
            })
            Swal.fire({
                "title": "Account Created",
                "text": "Account has been created successfully",
                "icon": "success"
            }).then(() => {
                onClose();
                window.location.reload();
            })
            
        } catch (error) {
          showApiErrors(error)
        } finally {
            setCreatingAccount(false)
        }
    }

    useEffect(()=>{
        if(memberSearch?.length > 0){
            getRegisteredMembers(memberSearch);
        }
    }, [memberSearch]);

    if (!show) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-xl p-6 max-w-md w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-semibold mb-4">Create User Account</h3>
          
          <form onSubmit={createUser} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                onChange={handleChange}
                name="username"
                id="username"
                placeholder="Username"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  placeholder="Password"
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? (
                    <FaRegEyeSlash size={20} />
                  ) : (
                    <FaRegEye size={20} />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select 
                required 
                name="role" 
                id="role" 
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Role</option>
                <option value="Administrator">
                  Administrator
                </option>
                <option value="Administrative Assistant">
                  Administrative Assistant
                </option>
                <option value="Finance Manager">
                  Finance Manager
                </option>
                <option value="Welfare Manager">
                  Welfare Manager
                </option>
                <option value="Usher">
                  Usher
                </option>
              </select>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Member
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setMemberDropdownOpen(!memberDropdownOpen)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between bg-white"
                >
                  <span className={selectedMember ? "text-gray-900" : "text-gray-500"}>
                    {selectedMember ? `${selectedMember.full_name}` : "Select Member"}
                  </span>
                  <IoChevronDownSharp 
                    size={20} 
                    className={`text-gray-400 transition-transform ${memberDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {memberDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <CiSearch size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search members..."
                          value={memberSearch}
                          onChange={(e) => setMemberSearch(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                    {
                        memberSearch?.length > 2 ? (
                            <div className="max-h-48 overflow-y-auto">
                            {registeredMembers?.length > 0 ? (
                                registeredMembers.map((member) => (
                                <button
                                    key={member.id}
                                    type="button"
                                    onClick={() => handleMemberSelect(member)}
                                    className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm"
                                >
                                    {member.full_name}
                                </button>
                                ))
                            ) : (
                                <div className="px-3 py-2 text-gray-500 text-sm">
                                   {searching ? "Searching...":"No member found"}
                                </div>
                            )}
                            </div>
                        ):(
                            <div className="px-3 py-2 text-gray-500 text-sm">
                                Enter at least 3 characters to search for members
                            </div>
                        )
                    }
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              
              {creatingAccount ? (
                <div className="flex items-center justify-center px-4 py-2">
                  <ThreeDots
                    height="20"
                    width="40"
                    radius="9"
                    color="#2563eb"
                    ariaLabel="three-dots-loading"
                    visible={true}
                  />
                </div>
              ) : (
                <button 
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create User
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
};

export default AddUserModal;